import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { useSelector } from "react-redux";
import { TriangleExclamationSolidV6 } from "@icons/index";
import Button from "@shared/v2/Button";
import TextButton from "@shared/v2/TextButton";
import * as css from "./person-detail-styles.module.css";
import MarketReportCard from "../../shared/MarketReport/MarketReportCard";
import MarketReportPreview from "../../MarketReports/MarketReportPreview";
import CollapsibleElement from "./CollapsibleElement";
import Loading from "../../Goals/components/Loader";

const getApiHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  "x-csrf-token": window.ReactOnRails.authenticityToken(),
});

const useMarketReports = ({ person, refreshKey }) => {
  const [marketReports, setMarketReports] = useState([]);
  const [isMarketReportModalOpen, setIsMarketReportModalOpen] = useState(false);

  useEffect(() => {
    if (!person?.uuid) return;
    axios
      .get(`/market_reports?uuid=${person?.uuid}`, { headers: getApiHeaders() })
      .then(({ data }) => setMarketReports(data))
      .catch(console.log);
  }, [person?.uuid, refreshKey]);

  return {
    actions: {
      deleteMarketReport: ({ id, primaryAgent }) =>
        axios
          .post(
            "/market_reports/delete_report",
            { id, primary_agent_uuid: primaryAgent },
            { headers: getApiHeaders() },
          )
          .then(() => setMarketReports((currentReports) => currentReports.filter((r) => r.id !== id)))
          .catch((err) => console.log(err)),
      toggleModal: (value) => setIsMarketReportModalOpen((o) => (typeof value === "boolean" ? value : !o)),
    },
    data: { marketReports, isMarketReportModalOpen },
  };
};

const MarketReports = ({
  editMarketReport,
  openMarketReport,
  refreshKey,
  editMarketReportPanel2,
  hasApprovedMls,
  loading,
}) => {
  const basePerson = useSelector((state) => state.personDetail.person);
  const person = {
    ...basePerson.data?.attributes,
    status: basePerson.data?.attributes.status,
    person_avatar: basePerson.data?.attributes.avatar_url,
    lead_id: basePerson.data?.attributes.lead.id,
    person_lead_type: basePerson.data?.attributes.lead_type?.charAt(0),
    new_market_report_link: basePerson.data?.attributes.new_market_report_link,
    display_a_street_address: basePerson?.included
      ?.filter((i) => i.type === "address" && i.id !== null)
      .map((i) => `${i.attributes?.street_address}`)[0],
  };
  const {
    actions,
    data: { isMarketReportModalOpen, marketReports },
  } = useMarketReports({ person, refreshKey });
  const primaryAgent = basePerson.data?.attributes.primary_agent_uuid;
  const hasEmail = Boolean(basePerson.data?.attributes.main_email);
  // Disabling before the person loads is safe; claiming they have no email is not.
  const personLoaded = Boolean(basePerson.data);

  const mostRecentReport = useMemo(() => {
    const viewedReports = marketReports.filter((report) => report.most_recent_view !== null);
    if (viewedReports.length > 1) {
      return viewedReports.sort((a, b) => new Date(b.most_recent_view) - new Date(a.most_recent_view))[0];
    }
    return marketReports[0];
  }, [marketReports]);

  const openLink = () => {
    window.open(
      "https://www.brivityknowledge.com/v1/docs/en/set-upedit-your-cma-profile?highlight=cma%20profile",
      "_blank",
    );
  };

  return (
    <CollapsibleElement
      id="pdp-market-reports"
      title="Market Reports"
      buttonText="+ ADD"
      buttonId="market-report-add"
      buttonClickFunction={openMarketReport}
      buttonDisabled={!hasApprovedMls || !hasEmail}
      // The MLS banner in the card body already explains the !hasApprovedMls case.
      buttonDisabledTooltip={
        personLoaded && hasApprovedMls ? "Contact does not have an email address" : null
      }
    >
      {loading && <Loading />}
      <div id="person-market-reports-card">
        {!loading && (!hasApprovedMls || marketReports.length === 0) ? (
          <div>
            {!hasApprovedMls && (
              <div className="tw-py-[12px] tw-px-[16px] tw-border-solid tw-border-[1px] tw-text-[#E5941B] tw-border-semantic-yellow-10 tw-bg-semantic-yellow-5 tw-rounded-sm tw-flex tw-items-center">
                <TriangleExclamationSolidV6 size="l" />
                <div>
                  <div className="tw-pl-[16px] tw-mb-[8px] tw-text-[12px] tw-leading-[16px] tw-text-semantic-yellow-120">
                    To start using Market Reports, your CMA profile must be set up.
                  </div>
                  <Button schema="semantic-yellow" onClick={openLink} className="tw-ml-[16px]">
                    <span>SET UP MY PROFILE</span>
                  </Button>
                </div>
              </div>
            )}

            <div className={`tw-pt-[8px] tw-pb-[4px] ${css.personDetailEmptyStateText}`}>
              Send Market Reports to contacts to let them know what’s happening in the real estate market in
              their area.
            </div>
          </div>
        ) : (
          <MarketReportCard
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...mostRecentReport}
            editReportFn={(id) => editMarketReport(id)}
            editMarketReportPanel2={editMarketReportPanel2}
            deleteReportFn={(id) => actions.deleteMarketReport({ id, primaryAgent })}
            showAdjustment
          />
        )}

        {hasApprovedMls && marketReports.length > 1 && (
          <TextButton className="tw-px-0 tw-mt-[8px]" onClick={() => actions.toggleModal(true)}>
            View All
          </TextButton>
        )}

        {isMarketReportModalOpen && (
          <MarketReportPreview
            modalIsOpen
            modalOnly
            onClose={() => actions.toggleModal(false)}
            person={person}
            person_status={person.status}
            person_address={person.display_a_street_address}
            person_lead_type={person.person_lead_type}
            person_avatar={person.person_avatar}
            editReportFn={(id) => editMarketReport(id)}
            editMarketReportPanel2={editMarketReportPanel2}
            deleteReportFn={(id) => actions.deleteMarketReport({ id, primaryAgent })}
            openMarketReport={() => openMarketReport()}
          />
        )}
      </div>
    </CollapsibleElement>
  );
};

MarketReports.propTypes = {
  editMarketReport: PropTypes.func.isRequired,
  openMarketReport: PropTypes.func.isRequired,
  refreshKey: PropTypes.string.isRequired,
  editMarketReportPanel2: PropTypes.func.isRequired,
  hasApprovedMls: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
};

export default MarketReports;
